import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      {/* Top bar */}
      <div className="bg-gray-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:6479934955" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">647-993-4955</span>
            </a>
            <a href="mailto:Shawstransportlogistics@gmail.com" className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Mail size={14} />
              <span className="hidden sm:inline">Shawstransportlogistics@gmail.com</span>
            </a>
          </div>
          <div className="text-xs sm:text-sm">
            <span className="text-orange-400 font-semibold">Available 24/7</span> for urgent deliveries
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
            <img 
              src="https://customer-assets.emergentagent.com/job_ltl-logistics/artifacts/eqm9a6g7_Shaws%20Logistics%20final%20logo%20white%20background%202%20with%20road.jpg"
              alt="Shaw Transport & Logistics"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
              Services
            </button>
            <button onClick={() => navigate('/medical-courier')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
              Medical Courier
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
              About
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
              Testimonials
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
              FAQ
            </button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Get A Quote
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-left">
                Services
              </button>
              <button onClick={() => { navigate('/medical-courier'); setIsMobileMenuOpen(false); }} className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-left">
                Medical Courier
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-left">
                About
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-left">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-left">
                FAQ
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-orange-500 hover:bg-orange-600 text-white w-full"
              >
                Get A Quote
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
